import { toast } from '@askable/ui/core/sonner';
import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { activeDownloadsAtom, downloadQueueAtom } from '../data/downloadQueue.atom';
import { pollingRegistryAtom } from '../data/pollingRegistry.atom';

import { useDownloadExecutor } from './useDownloadExecutor';

import type { PollingConfig } from '../data/pollingRegistry.atom';
import type { DownloadItem, DownloadQueueState } from '../types';

const POLL_CHECK_INTERVAL = 250;

/**
 * Global polling mechanism that runs in the provider
 * Uses a single stable interval that checks which downloads need polling
 * Each download is polled at its own interval without recreating the main interval
 */
export const useDownloadPoller = () => {
  const { t } = useTranslation();
  const activeDownloads = useAtomValue(activeDownloadsAtom);
  const pollingRegistry = useAtomValue(pollingRegistryAtom);
  const setDownloadQueue = useSetAtom(downloadQueueAtom);
  const setPollingRegistry = useSetAtom(pollingRegistryAtom);
  const { executeDownload } = useDownloadExecutor();

  // Track last poll time for each download
  const lastPollTimesRef = useRef<Record<string, number>>({});
  // Track poll attempt counts
  const pollCountsRef = useRef<Record<string, number>>({});
  // Prevent concurrent polling of the same download
  const pollingInProgressRef = useRef<Set<string>>(new Set());

  const handleTimeout = useCallback(
    (download: DownloadItem) => {
      // Dismiss loading toast FIRST before any state updates
      const toastId = download.metadata?.toastId;
      if (toastId) {
        toast.dismiss(toastId);
      }

      setDownloadQueue((prev: DownloadQueueState) => {
        const newActivePollingIds = new Set(prev.activePollingIds);
        newActivePollingIds.delete(download.id);

        return {
          downloads: {
            ...prev.downloads,
            [download.id]: {
              ...prev.downloads[download.id],
              status: 'error',
              error: {
                message: 'Polling timeout: maximum attempts exceeded',
                code: 'POLLING_TIMEOUT',
              },
              completedAt: Date.now(),
            },
          },
          activePollingIds: newActivePollingIds,
        };
      });

      setPollingRegistry((prev: Record<string, PollingConfig>) => {
        const { [download.id]: _, ...newRegistry } = prev;
        return newRegistry;
      });

      // Clean up tracking refs
      const { [download.id]: _pollCount, ...remainingPollCounts } = pollCountsRef.current;
      pollCountsRef.current = remainingPollCounts;
      const { [download.id]: _lastPoll, ...remainingLastPolls } = lastPollTimesRef.current;
      lastPollTimesRef.current = remainingLastPolls;
      // Don't delete pollingInProgressRef here - let the finally block handle it
      // to prevent race conditions where the download gets polled again before cleanup

      if (!download.metadata?.silent) {
        toast.error(t('components.downloadManager.errorToastTimeout'));
      }
    },
    [setDownloadQueue, setPollingRegistry, t],
  );

  const handleError = useCallback(
    (download: DownloadItem, errorMessage: string) => {
      // Dismiss loading toast FIRST before any state updates
      const toastId = download.metadata?.toastId;
      if (toastId) {
        toast.dismiss(toastId);
      }

      setDownloadQueue((prev: DownloadQueueState) => {
        const newActivePollingIds = new Set(prev.activePollingIds);
        newActivePollingIds.delete(download.id);

        return {
          downloads: {
            ...prev.downloads,
            [download.id]: {
              ...prev.downloads[download.id],
              status: 'error',
              error: { message: errorMessage },
              completedAt: Date.now(),
            },
          },
          activePollingIds: newActivePollingIds,
        };
      });

      setPollingRegistry((prev: Record<string, PollingConfig>) => {
        const { [download.id]: _, ...newRegistry } = prev;
        return newRegistry;
      });

      // Clean up tracking refs
      const { [download.id]: _pollCount, ...remainingPollCounts } = pollCountsRef.current;
      pollCountsRef.current = remainingPollCounts;
      const { [download.id]: _lastPoll, ...remainingLastPolls } = lastPollTimesRef.current;
      lastPollTimesRef.current = remainingLastPolls;
      // Don't delete pollingInProgressRef here - let the finally block handle it
      // to prevent race conditions where the download gets polled again before cleanup

      datadogRum.addError(new Error('Polling failed'), { downloadId: download.id, errorMessage });
      if (!download.metadata?.silent) {
        toast.error(t('components.downloadManager.errorToastError'));
      }
    },
    [setDownloadQueue, setPollingRegistry, t],
  );

  const pollDownload = useCallback(
    async (download: DownloadItem, config: PollingConfig) => {
      // Prevent concurrent polling of the same download
      if (pollingInProgressRef.current.has(download.id)) {
        return;
      }

      const now = Date.now();
      const lastPoll = lastPollTimesRef.current[download.id] || 0;
      const timeSinceLastPoll = now - lastPoll;

      // Check if enough time has elapsed since last poll
      if (timeSinceLastPoll < config.pollInterval) {
        return;
      }

      // Mark as currently polling
      pollingInProgressRef.current.add(download.id);
      lastPollTimesRef.current = { ...lastPollTimesRef.current, [download.id]: now };

      // Initialize poll count
      if (pollCountsRef.current[download.id] === undefined) {
        pollCountsRef.current = { ...pollCountsRef.current, [download.id]: 0 };
      }

      // Check max attempts
      if (pollCountsRef.current[download.id] >= config.maxPollAttempts) {
        handleTimeout(download);
        pollingInProgressRef.current.delete(download.id);
        return;
      }

      try {
        // Execute poll function - increment count
        pollCountsRef.current = {
          ...pollCountsRef.current,
          [download.id]: (pollCountsRef.current[download.id] || 0) + 1,
        };
        const pollResult = await config.pollFn();
        const statusResponse = config.getStatusFromPoll(pollResult);

        if (statusResponse.status === 'ready') {
          // Extract download URL from poll result
          // Use getDownloadUrlFromPoll if available, otherwise fall back to getDownloadUrl
          const downloadUrl =
            config.getDownloadUrlFromPoll?.(pollResult) ?? config.getDownloadUrl?.(pollResult) ?? undefined;

          if (!downloadUrl) {
            throw new Error('No download URL available');
          }

          // Update status to ready with download URL
          setDownloadQueue((prev: DownloadQueueState) => ({
            ...prev,
            downloads: {
              ...prev.downloads,
              [download.id]: {
                ...prev.downloads[download.id],
                status: 'ready',
                downloadUrl,
              },
            },
          }));

          const updatedDownload = {
            ...download,
            status: 'ready' as const,
            downloadUrl,
          };

          // Execute download
          executeDownload(updatedDownload);

          // Clean up tracking refs
          const { [download.id]: _successPollCount, ...remainingSuccessPollCounts } = pollCountsRef.current;
          pollCountsRef.current = remainingSuccessPollCounts;
          const { [download.id]: _successLastPoll, ...remainingSuccessLastPolls } = lastPollTimesRef.current;
          lastPollTimesRef.current = remainingSuccessLastPolls;
        } else if (statusResponse.status === 'error') {
          handleError(download, statusResponse.error.message);
        } else if (statusResponse.status === 'pending') {
          // Continue polling, update status to polling
          setDownloadQueue((prev: DownloadQueueState) => ({
            ...prev,
            downloads: {
              ...prev.downloads,
              [download.id]: {
                ...prev.downloads[download.id],
                status: 'polling',
                metadata: {
                  ...prev.downloads[download.id].metadata,
                  progress: statusResponse.progress,
                },
              },
            },
          }));
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Polling failed';
        handleError(download, errorMessage);
      } finally {
        // Always clear the in-progress flag
        pollingInProgressRef.current.delete(download.id);
      }
    },
    [setDownloadQueue, executeDownload, handleTimeout, handleError],
  );

  useEffect(() => {
    const checkDownloads = () => {
      // Get downloads that need polling
      const pendingDownloads = activeDownloads.filter(
        d => (d.status === 'pending' || d.status === 'polling') && d.serverTaskId && pollingRegistry[d.id],
      );

      // Poll each download if its interval has elapsed
      for (const download of pendingDownloads) {
        const config = pollingRegistry[download.id];
        if (config) {
          void pollDownload(download, config);
        }
      }

      // Clean up refs for downloads that are no longer active
      const activeIds = new Set(pendingDownloads.map(d => d.id));
      const inactiveIds = Object.keys(lastPollTimesRef.current).filter(id => !activeIds.has(id));

      if (inactiveIds.length > 0) {
        // Remove inactive IDs from all refs
        lastPollTimesRef.current = Object.fromEntries(
          Object.entries(lastPollTimesRef.current).filter(([id]) => !inactiveIds.includes(id)),
        );
        pollCountsRef.current = Object.fromEntries(
          Object.entries(pollCountsRef.current).filter(([id]) => !inactiveIds.includes(id)),
        );
        inactiveIds.forEach(id => pollingInProgressRef.current.delete(id));
      }
    };

    // Initial check
    checkDownloads();

    // Set up interval to check which downloads need polling
    const intervalId = setInterval(checkDownloads, POLL_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [activeDownloads, pollingRegistry, pollDownload]);
};
